import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { UserPlus, Copy, CheckCircle2, X, Mail, Trash2, AlertTriangle, FileText, ShieldAlert, Download } from 'lucide-react'
import { Link } from 'react-router-dom'
import api from '../../services/api.js'
import { useAuth } from '../../hooks/useAuth'
import { formatDate, formatDateTime } from '../../utils/helpers'
import { copyText } from '../../utils/clipboard.js'
import FileViewerModal from '../common/FileViewerModal.jsx'

const STATUS_COLUMNS = [
  { key: 'applied', label: 'Applied', color: 'bg-yellow-100 border-yellow-300' },
  { key: 'under_review', label: 'Under Review', color: 'bg-blue-100 border-blue-300' },
  { key: 'onboarding', label: 'Onboarding', color: 'bg-purple-100 border-purple-300' },
  { key: 'active', label: 'Active', color: 'bg-green-100 border-green-300' },
  { key: 'suspended', label: 'Suspended', color: 'bg-red-100 border-red-300' },
]

// Maps a checklist item to the consent_type its signed document was stored under
// (backend: GET /onboarding/consent/{user_id}/{consent_type}/download).
const CHECKLIST_TO_CONSENT_TYPE = {
  kyc_consent_signed: 'kyc',
}

export default function OnboardingPipeline() {
  const { t } = useTranslation('onboarding')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const [pipeline, setPipeline] = useState({})
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [checklist, setChecklist] = useState([])

  // Ref for auto-scrolling to the detail panel when a consultant card is clicked
  const detailPanelRef = useRef(null)

  // Delete-user confirmation state (two-step modal)
  const [deleteStep, setDeleteStep] = useState(0)   // 0 = closed, 1 = first warning, 2 = final confirm
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const [cvFor, setCvFor] = useState(null)   // whose CV is open in the viewer

  useEffect(() => {
    fetchPipeline()
  }, [])

  const fetchPipeline = async () => {
    try {
      const { data } = await api.get('/onboarding/pipeline')
      setPipeline(data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const fetchChecklist = async (userId) => {
    try {
      const { data } = await api.get(`/onboarding/checklist/${userId}`)
      setChecklist(data)
    } catch (e) {
      console.error(e)
    }
  }

  const verifyItem = async (itemId) => {
    try {
      await api.put(`/onboarding/checklist/${itemId}/verify`, {})
      fetchChecklist(selected.id)
    } catch (e) {
      alert(e.response?.data?.detail || 'Error verifying item')
    }
  }

  const activateConsultant = async (userId) => {
    try {
      await api.put(`/onboarding/${userId}/activate`, {})
      fetchPipeline()
      setSelected(null)
    } catch (e) {
      alert(e.response?.data?.detail || 'Cannot activate: incomplete checklist items')
    }
  }

  const selectConsultant = (consultant) => {
    setSelected(consultant)
    fetchChecklist(consultant.id)
    setDeleteStep(0)  // reset any in-progress delete flow
    setDeleteError(null)
    // Smooth-scroll to the detail panel so admin sees it appear (was below the fold for some)
    setTimeout(() => {
      detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)  // small delay so the panel is mounted before scrolling
  }

  // Read the CV here rather than handing the browser a blob to deal with: most CVs are Word
  // files, and a browser answers those with a download — which is not "view".
  const viewCv = (user) => setCvFor(user)

  const downloadCv = async (userId, name) => {
    try {
      const resp = await api.get(`/users/${userId}/cv`, { responseType: 'blob' })
      const blobUrl = URL.createObjectURL(resp.data)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = `${(name || 'consultant').replace(/\s+/g, '_')}_CV`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not download CV.')
    }
  }

  // Download a signed consent document. The endpoint returns EITHER the .docx bytes
  // directly (local-disk fallback) OR a JSON {url, filename} pointing at a pre-signed
  // cloud-storage URL — inspect the actual content-type to tell which one came back.
  const downloadConsentDoc = async (userId, itemKey) => {
    const consentType = CHECKLIST_TO_CONSENT_TYPE[itemKey]
    if (!consentType) return
    try {
      const resp = await api.get(`/onboarding/consent/${userId}/${consentType}/download`, { responseType: 'blob' })
      const contentType = resp.data?.type || ''
      if (contentType.includes('application/json')) {
        const text = await resp.data.text()
        const { url } = JSON.parse(text)
        window.open(url, '_blank')
      } else {
        const blobUrl = URL.createObjectURL(resp.data)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = `${itemKey}.docx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
      }
    } catch (e) {
      // With responseType: 'blob', a 403/404 error body arrives as a Blob too, not
      // parsed JSON — pull the real "detail" message out of it instead of showing a
      // generic fallback for every failure.
      let detail = 'No signed document found for this step.'
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text()
          detail = JSON.parse(text)?.detail || detail
        } catch { /* keep the generic fallback */ }
      } else if (e.response?.data?.detail) {
        detail = e.response.data.detail
      }
      alert(detail)
    }
  }

  // View a signed consent in the browser: fetch the HTML with auth, then open it as a
  // blob URL in a new tab (a plain window.open wouldn't carry the auth token).
  const viewConsentDoc = async (userId, itemKey) => {
    const consentType = CHECKLIST_TO_CONSENT_TYPE[itemKey]
    if (!consentType) return
    try {
      const resp = await api.get(`/onboarding/consent/${userId}/${consentType}/view`, { responseType: 'blob' })
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: 'text/html' }))
      window.open(blobUrl, '_blank')
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    } catch (e) {
      let detail = 'Could not open this consent record.'
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail || detail } catch { /* keep fallback */ }
      } else if (e.response?.data?.detail) { detail = e.response.data.detail }
      alert(detail)
    }
  }

  // ── Delete consultant (two-step confirmation) ──────────────────────────
  const beginDelete = () => { setDeleteStep(1); setDeleteError(null) }
  const cancelDelete = () => { setDeleteStep(0); setDeleteError(null) }

  const confirmDelete = async () => {
    if (!selected) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await api.delete(`/users/${selected.id}/`)
      // Refresh pipeline + close detail panel
      await fetchPipeline()
      setSelected(null)
      setDeleteStep(0)
      setChecklist([])
    } catch (e) {
      setDeleteError(e.response?.data?.detail || 'Failed to delete user.')
    } finally {
      setDeleting(false)
    }
  }

  // ── Invite-consultant state (Phase G3) ─────────────────────────────────
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [inviteResult, setInviteResult] = useState(null) // { invite_url, expires_at, expires_in_hours }
  const [copied, setCopied] = useState(false)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('Consultant')

  const generateInvite = async () => {
    setInviting(true)
    setInviteResult(null)
    try {
      const r = await api.post('/onboarding/invites', {
        role: inviteRole,
        target_email: recipientEmail.trim() || null,
      })
      setInviteResult(r.data)
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate invite link')
    } finally {
      setInviting(false)
    }
  }

  const copyLink = async () => {
    if (!inviteResult?.invite_url) return
    const ok = await copyText(inviteResult.invite_url)
    if (ok) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } else {
      alert('Could not copy automatically — please select the link and copy it manually.')
    }
  }

  const closeInviteModal = () => {
    setShowInviteModal(false)
    setInviteResult(null)
    setCopied(false)
    setRecipientEmail('')
    setInviteRole('Consultant')
  }

  // Build the email subject + body once — reused by Gmail and mailto handlers.
  const buildEmailContent = () => {
    const roleLabel = inviteResult?.role || 'team member'
    const subject = `Welcome to ProTrack — Start your ${roleLabel} onboarding`
    const body = [
      `Hi,`,
      ``,
      `You've been invited to join 3 Pillars Data & Training LLC as a ${roleLabel}.`,
      ``,
      `Click the link below to create your account and begin onboarding:`,
      inviteResult.invite_url,
      ``,
      `Note: This is a single-use link and expires on ${formatDateTime(inviteResult.expires_at)}.`,
      ``,
      `If you have any questions, just reply to this email.`,
      ``,
      `Best regards,`,
      `3 Pillars Data & Training LLC`,
    ].join('\n')
    return { subject, body }
  }

  // Primary: open Gmail compose in a new tab — works without any OS config.
  const openGmail = () => {
    if (!inviteResult?.invite_url) return
    const { subject, body } = buildEmailContent()
    const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(recipientEmail.trim())}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  // Fallback: open the OS default mail handler (Outlook desktop, Apple Mail, etc.).
  // Only works if the user has a default mail app configured.
  const openDefaultMailApp = () => {
    if (!inviteResult?.invite_url) return
    const { subject, body } = buildEmailContent()
    const mailto = `mailto:${encodeURIComponent(recipientEmail.trim())}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    // Use an anchor click so popup blockers don't interfere
    const a = document.createElement('a')
    a.href = mailto
    a.click()
  }

  // Two levels: VIEW the pipeline (Admin / HR / view-people / manage-onboarding) vs
  // INVITE new people (Admin or the onboard_users capability). HR tracks onboarding but
  // does not mint invites, so it sees the board without the "Invite Team Member" button.
  const _caps = user?.capabilities || []
  const canView = user && (user.role === 'Admin' || user.role === 'HR'
    || _caps.includes('onboard_users') || _caps.includes('view_people') || _caps.includes('manage_onboarding'))
  const canInvite = user && (user.role === 'Admin' || _caps.includes('onboard_users') || user.role === 'HR')
  // Split the two halves of the onboarding chain so neither role is shown a button that 403s.
  // HR VERIFIES the documents (KYC screening is their job); only an Admin ACTIVATES, and only
  // after every item has been verified — the screener and the access-granter are different
  // people on purpose.
  const canVerify = user && (user.role === 'Admin' || user.role === 'HR' || _caps.includes('manage_onboarding'))
  const canActivate = user && user.role === 'Admin'
  // HR may onboard only Consultant/HR; Admin/onboard_users may onboard any stakeholder role.
  const inviteRoleOptions = (user?.role === 'HR' && !_caps.includes('onboard_users'))
    ? ['Consultant', 'HR']
    // 'Partner' is an EXTERNAL person (e.g. UNAIDS) who only works on data files for one product.
    // Invite-only by design — it is deliberately absent from public sign-up.
    : ['Consultant', 'Project Manager', 'Finance', 'Funder', 'HR', 'Partner']
  if (user && !canView) {
    const isConsultant = user.role === 'Consultant'
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto bg-white border border-gray-200 rounded-xl p-8 text-center mt-10">
          <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-800">{t('pipeline2.admin_only')}</h2>
          <p className="text-sm text-gray-500 mt-1">{t('pipeline2.managed_by_admin')}</p>
          {/* Only a consultant has a personal onboarding page — don't funnel PM/Finance/Funder
              into the consultant flow (that's what made a PM see "My Onboarding"). */}
          {isConsultant && (
            <Link to="/onboarding/my" className="inline-block mt-4 text-sm text-blue-600 hover:underline">{t('pipeline2.go_my_onboarding')}</Link>
          )}
        </div>
      </div>
    )
  }

  if (loading) return <div className="p-6 text-gray-500">{t('pipeline2.pipeline_loading')}</div>

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('pipeline2.pipeline_title')}</h1>
        {canInvite && (
          <button
            onClick={() => { setShowInviteModal(true) }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-linear-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg shadow-blue-500/25 hover:from-blue-700 hover:to-indigo-700"
          >
            <UserPlus className="w-4 h-4" />
            Invite Team Member
          </button>
        )}
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {STATUS_COLUMNS.map(col => (
          <div key={col.key} className={`min-w-52 shrink-0 rounded-xl border-2 p-3 ${col.color}`}>
            <h3 className="font-semibold text-gray-700 mb-3 text-sm uppercase tracking-wide">
              {col.label}
              <span className="ml-2 bg-white rounded-full px-2 py-0.5 text-xs font-bold">
                {(pipeline[col.key] || []).length}
              </span>
            </h3>
            <div className="space-y-2">
              {(pipeline[col.key] || []).map(c => (
                <div
                  key={c.id}
                  onClick={() => selectConsultant(c)}
                  className="bg-white rounded-lg p-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                >
                  <p className="font-medium text-gray-800 text-sm">{c.full_name}</p>
                  {c.consultant_code && (
                    <p className="text-[10px] font-mono text-gray-400">{c.consultant_code}</p>
                  )}
                  <p className="text-xs text-gray-500 truncate">{c.email}</p>
                  {c.specialization && (
                    <span className="text-xs bg-gray-100 text-gray-600 rounded px-1 mt-1 inline-block">
                      {c.specialization}
                    </span>
                  )}
                </div>
              ))}
              {(pipeline[col.key] || []).length === 0 && (
                <p className="text-xs text-gray-400 italic text-center py-2">{t('pipeline2.empty')}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Detail panel */}
      {selected && (
        <div ref={detailPanelRef} className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm p-6 scroll-mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-800">{selected.full_name}</h2>
              {selected.consultant_code && (
                <p className="text-xs font-mono text-gray-400">{selected.consultant_code}</p>
              )}
              <p className="text-sm text-gray-500">{selected.email}</p>
              <span className="inline-block mt-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 uppercase tracking-wide">
                {selected.consultant_status || 'Active'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {selected.has_cv && (
                <button
                  onClick={() => viewCv(selected)}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100"
                  title={t('pipeline2.t_view_cv')}
                >
                  <FileText className="w-4 h-4" /> View CV
                </button>
              )}
              {selected.consultant_status !== 'Active' && canActivate && (
                <button
                  onClick={() => activateConsultant(selected.id)}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700"
                >
                  Activate Consultant
                </button>
              )}
              {selected.consultant_status !== 'Active' && !canActivate && canVerify && (
                <span className="text-xs text-gray-500 border border-gray-200 bg-gray-50 rounded-lg px-3 py-2">
                  Verify the documents below — an Admin then activates the account.
                </span>
              )}
              <button
                onClick={beginDelete}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-red-700 border border-red-200 bg-red-50 hover:bg-red-100"
                title={t('pipeline2.t_remove_consultant')}
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-400 hover:text-gray-600 px-3 py-2 text-sm"
              >
                Close
              </button>
            </div>
          </div>

          <h3 className="font-semibold text-gray-700 mb-3">{t('pipeline2.checklist_title')}</h3>
          <div className="space-y-2">
            {checklist.map(item => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    item.completed ? 'bg-green-500' : 'bg-gray-300'
                  }`}>
                    {item.completed && <span className="text-white text-xs">✓</span>}
                  </div>
                  <span className={`text-sm ${item.completed ? 'text-gray-800' : 'text-gray-500'}`}>
                    {item.item_label}
                  </span>
                </div>
                {/* Also for a step the consultant finished themselves (identity, consent): done is not
                    checked. Activation needs every step checked by somebody, so hiding the button
                    for finished steps left an Admin with no way to activate a normal sign-up. */}
                {(!item.completed || !item.verified_by) && canVerify && (
                  <button
                    onClick={() => verifyItem(item.id)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Mark Verified
                  </button>
                )}
                {item.completed && item.verified_by && (
                  <span className="text-[11px] text-green-700" title={t('pipeline2.t_checked_by_hr')}>
                    ✓ verified
                  </span>
                )}
                {item.completed && (
                  <div className="flex items-center gap-3">
                    {CHECKLIST_TO_CONSENT_TYPE[item.item_key] && (
                      <>
                        <button
                          onClick={() => viewConsentDoc(selected.id, item.item_key)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                          title={t('pipeline2.t_open_consent')}
                        >
                          <FileText className="w-3.5 h-3.5" /> View
                        </button>
                        <button
                          onClick={() => downloadConsentDoc(selected.id, item.item_key)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                          title={t('pipeline2.t_download_consent')}
                        >
                          <Download className="w-3.5 h-3.5" /> Download
                        </button>
                      </>
                    )}
                    {item.completed_at && (
                      <span className="text-xs text-gray-400">
                        {formatDate(item.completed_at)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Invite Consultant Modal (Phase G3) ─────────────────────────── */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">{t('pipeline2.invite_member')}</h2>
                  <p className="text-xs text-gray-500">{t('pipeline2.pick_role_share')}</p>
                </div>
              </div>
              <button onClick={closeInviteModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step 1 — choose role + (optional) email, then generate */}
            {!inviting && !inviteResult && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{t('pipeline2.role_to_onboard')}</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="w-full text-sm bg-white border border-gray-300 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {inviteRoleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{t('pipeline2.invitee_email')}</label>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="person@example.com"
                    className="w-full text-sm bg-white border border-gray-300 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <button onClick={closeInviteModal} className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">{tc('actions.cancel')}</button>
                  <button
                    onClick={generateInvite}
                    className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Generate invite link
                  </button>
                </div>
              </div>
            )}

            {inviting && (
              <div className="py-8 text-center">
                <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-500 mt-2">{t('pipeline2.generating_link')}</p>
              </div>
            )}

            {!inviting && inviteResult && (
              <div className="space-y-4">
                {/* Send via Email — opens an email composer with everything pre-filled */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wide font-semibold text-blue-700 mb-1.5 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Send via email
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    Type the consultant's email — we'll open Gmail (or your default mail app) with the message pre-filled.
                  </p>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder={t('pipeline2.ph_consultant_email')}
                    className="w-full text-sm bg-white border border-gray-300 rounded-md px-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={openGmail}
                      disabled={!recipientEmail.trim()}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Mail className="w-3.5 h-3.5" /> Open in Gmail
                    </button>
                    <button
                      onClick={openDefaultMailApp}
                      disabled={!recipientEmail.trim()}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 border border-blue-200 bg-white rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Opens your OS default mail app (Outlook desktop, Apple Mail, etc.). Requires a default mail handler set in your OS."
                    >
                      Default mail app
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1.5">
                    Tip: "Open in Gmail" works in any browser. "Default mail app" needs Outlook/Apple Mail set as your system default.
                  </p>
                </div>

                {/* Or copy the link manually */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1">{t('pipeline2.or_copy_link')}</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={inviteResult.invite_url}
                      className="flex-1 text-xs font-mono bg-white border border-gray-200 rounded-md px-2 py-1.5 text-gray-700"
                      onClick={e => e.target.select()}
                    />
                    <button
                      onClick={copyLink}
                      className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${copied ? 'bg-green-100 text-green-700' : 'bg-gray-700 text-white hover:bg-gray-800'}`}
                    >
                      {copied ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                    </button>
                  </div>
                </div>

                <div className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  ⏱ This single-use link expires in <strong>{inviteResult.expires_in_hours} hours</strong>
                  ({formatDateTime(inviteResult.expires_at)}).
                </div>

                <div className="text-xs text-gray-600 leading-relaxed">
                  <p className="font-semibold mb-1">{t('pipeline2.what_happens')}</p>
                  <ol className="list-decimal list-inside space-y-0.5">
                    <li>Email or message this link to the {inviteResult.role || 'invitee'}.</li>
                    <li>{t('pipeline2.they_click_land')}</li>
                    <li>{t('pipeline2.after_registering')} <strong>{inviteResult.role || 'selected'}</strong> role{(inviteResult.role || 'Consultant') === 'Consultant' ? ' (status Applied, with the onboarding checklist)' : ''}.</li>
                    <li>{t('pipeline2.you_verify')}</li>
                  </ol>
                </div>

                <div className="flex justify-end pt-2 border-t">
                  <button onClick={closeInviteModal} className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Delete Consultant — Two-step confirmation modal ───────────── */}
      {deleteStep > 0 && selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={!deleting ? cancelDelete : undefined}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-gray-900">
                  {deleteStep === 1 ? 'Delete this consultant?' : '⚠️ Final confirmation'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {deleteStep === 1 ? (
                    <>{t('pipeline2.about_to_delete')} <strong className="text-gray-900">{selected.full_name}</strong> ({selected.email}).</>
                  ) : (
                    <>{t('pipeline2.last_chance')} <strong className="text-red-700">"Yes, Permanently Delete"</strong> to proceed.</>
                  )}
                </p>
              </div>
            </div>

            {deleteStep === 1 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900">
                <p className="font-semibold mb-1">{t('pipeline2.will_remove')}</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>{t('pipeline2.rm_account')}</li>
                  <li>{t('pipeline2.rm_checklist')}</li>
                  <li>{t('pipeline2.rm_banking')}</li>
                  <li>{t('pipeline2.rm_consent')}</li>
                  <li>{t('pipeline2.rm_notifications')}</li>
                </ul>
                <p className="mt-2 italic">Cannot delete if they have an Active or Pending contract — terminate it first.</p>
              </div>
            )}

            {deleteStep === 2 && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 mb-4 text-xs text-red-900">
                This action is <strong>irreversible</strong>. The consultant's data will be wiped and they will need a fresh invite to rejoin.
              </div>
            )}

            {deleteError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
                {deleteError}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              {deleteStep === 1 ? (
                <button
                  onClick={() => setDeleteStep(2)}
                  disabled={deleting}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
                >
                  Delete
                </button>
              ) : (
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm bg-red-700 text-white rounded-lg font-semibold hover:bg-red-800 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                  {deleting ? 'Deleting…' : 'Yes, Permanently Delete'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* The CV, rendered here rather than handed to the browser — Word files and slide decks
          keep their own layout instead of arriving in the downloads folder. */}
      {cvFor && (
        <FileViewerModal
          open={!!cvFor}
          onClose={() => setCvFor(null)}
          fetchUrl={`/users/${cvFor.id}/cv`}
          filename={(cvFor.resume_url || '').split('/').pop() || `${cvFor.full_name || 'Consultant'} CV`}
          onDownload={() => downloadCv(cvFor.id, cvFor.full_name)}
        />
      )}
    </div>
  )
}
